import React, { useState, useEffect } from "react";
import { X, AlertCircle } from "lucide-react";
import { FiChevronDown } from "react-icons/fi";
import { updateCustomerBilling } from "../../api/profile";
import {
  INDIAN_STATES,
  GSTIN_REGEX,
  getStateFromGSTIN,
} from "../../constants/gstStateCodes";

export default function HostBillingModal({
  isOpen,
  onClose,
  initialBillingDetails,
  onSuccess,
}) {
  const [billingForm, setBillingForm] = useState({
    billingName: "",
    gstNumber: "",
    address: "",
    state: "",
    pincode: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setError("");
      setBillingForm({
        billingName: initialBillingDetails?.billingName || "",
        gstNumber: initialBillingDetails?.gstNumber || "",
        address: initialBillingDetails?.address || "",
        state: initialBillingDetails?.state || "",
        pincode: initialBillingDetails?.pincode || "",
      });
    }
  }, [isOpen, initialBillingDetails]);

  if (!isOpen) return null;

  // Auto-detection of state from GSTIN
  const cleanGst = (billingForm.gstNumber || "").trim().toUpperCase();
  const detectedInfo = cleanGst ? getStateFromGSTIN(cleanGst) : null;
  const isStateLocked = Boolean(detectedInfo);
  const displayState = detectedInfo ? detectedInfo.state : billingForm.state;

  const handleBillingNameChange = (e) => {
    setBillingForm((prev) => ({ ...prev, billingName: e.target.value }));
    setError("");
  };

  const handleGstChange = (e) => {
    const val = e.target.value.toUpperCase();
    setBillingForm((prev) => {
      const next = { ...prev, gstNumber: val };
      const detected = getStateFromGSTIN(val);
      if (detected) {
        next.state = detected.state;
      }
      return next;
    });
    setError("");
  };

  const handleBillingAddressChange = (e) => {
    setBillingForm((prev) => ({ ...prev, address: e.target.value }));
    setError("");
  };

  const handleStateChange = (e) => {
    if (!isStateLocked) {
      setBillingForm((prev) => ({ ...prev, state: e.target.value }));
      setError("");
    }
  };

  const handlePincodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setBillingForm((prev) => ({ ...prev, pincode: val }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const name = billingForm.billingName?.trim();
    if (!name || name.length < 2) {
      setError("Please enter your Billing / Entity Name (minimum 2 characters).");
      return;
    }

    if (cleanGst) {
      if (!GSTIN_REGEX.test(cleanGst)) {
        setError("Invalid GSTIN format. Must be a valid 15-character Indian GST number.");
        return;
      }
      if (!detectedInfo) {
        setError("Invalid state code in GSTIN. First 2 digits must be a valid Indian state code.");
        return;
      }
    }

    const addr = billingForm.address?.trim();
    if (!addr || addr.length < 5) {
      setError("Please enter your complete billing address (minimum 5 characters).");
      return;
    }

    const st = cleanGst ? detectedInfo?.state : billingForm.state;
    if (!st) {
      setError("Please select your Billing State.");
      return;
    }

    const pin = billingForm.pincode?.trim();
    if (!pin || !/^[1-9][0-9]{5}$/.test(pin)) {
      setError("Please enter a valid 6-digit PIN code.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        billingName: name,
        gstNumber: cleanGst || null,
        address: addr,
        state: st,
        pincode: pin,
      };

      const res = await updateCustomerBilling(payload);
      const updatedDetails = res?.billingDetails || payload;

      if (onSuccess) {
        await onSuccess(updatedDetails);
      }
      onClose();
    } catch (err) {
      console.error("Failed to update billing details:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update billing details";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs z-[900]"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="fixed inset-0 z-[901] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-auto border border-gray-100"
          style={{ animation: "blModalIn 0.2s ease" }}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-gray-100">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">
                Billing & Tax Details Required
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Please enter your billing details to generate and download your statement.
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 transition shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-xs sm:text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Billing / Entity Name */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                Billing / Entity Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                disabled={saving}
                value={billingForm.billingName}
                onChange={handleBillingNameChange}
                placeholder="Rahul Mehta or Apex Events LLP"
                className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm bg-white text-gray-900 focus:ring-2 focus:ring-[#FF4C18]/20 focus:border-[#FF4C18] transition"
              />
            </div>

            {/* GSTIN (Optional) */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                GSTIN <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                maxLength={15}
                disabled={saving}
                value={billingForm.gstNumber}
                onChange={handleGstChange}
                placeholder="Enter your 15-character GSTIN"
                className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm uppercase font-mono tracking-wider bg-white text-gray-900 focus:ring-2 focus:ring-[#FF4C18]/20 focus:border-[#FF4C18] transition"
              />
            </div>

            {/* Billing Address */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                Billing Address <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                disabled={saving}
                value={billingForm.address}
                onChange={handleBillingAddressChange}
                placeholder={"Mr. Ashok Jha, 12-B, Sri Krishna Vihar Apartments, Sriharipuram,\nVisakhapatnam, India - 530011"}
                className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm resize-none bg-white text-gray-900 focus:ring-2 focus:ring-[#FF4C18]/20 focus:border-[#FF4C18] transition"
              />
            </div>

            {/* 2-Column: Billing State & PIN Code */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Billing State */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                  Billing State <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    disabled={saving || isStateLocked}
                    value={displayState}
                    onChange={handleStateChange}
                    className={`w-full px-3.5 py-2 border rounded-xl text-sm appearance-none pr-9 transition ${
                      isStateLocked
                        ? "bg-gray-50 border-gray-200 text-gray-700 cursor-not-allowed"
                        : "bg-white border-gray-300 text-gray-800 focus:ring-2 focus:ring-[#FF4C18]/20 focus:border-[#FF4C18]"
                    }`}
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                    <FiChevronDown className="w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* PIN Code */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                  PIN Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={6}
                  disabled={saving}
                  value={billingForm.pincode}
                  onChange={handlePincodeChange}
                  placeholder="440006"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm bg-white text-gray-900 focus:ring-2 focus:ring-[#FF4C18]/20 focus:border-[#FF4C18] transition"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                disabled={saving}
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#FF4C18] text-white px-5 py-2 rounded-xl hover:bg-[#FF4C18]/90 font-medium text-sm shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Saving & Downloading...</span>
                  </>
                ) : (
                  <span>Submit & Download</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
